/**
 * @fileoverview Fee schedule document service for system-web department documents.
 * @module system-web/services/feeScheduleService
 *
 * Responsibilities: - Own all fee-schedule server reads for the DepartmentDocumentsTab
 *                     surface (document list + per-document rules lazy-load).
 * Dependencies:     - ../apiClient (apiFetch)
 * Architecture:     - Online-exception reads — fee schedule documents are uploaded
 *                     binary-backed records, not Dexie-mirrored local-first data.
 *                     Components call these wrappers instead of apiFetch directly.
 * Notes:            - Document upload uses apiPostFormData from the component —
 *                     file upload is an inherently-online operation exempt from
 *                     the local-first service requirement.
 */

import { apiFetch } from '../apiClient';

/**
 * @process listFeeScheduleDocuments
 * Purpose: Fetch all fee schedule documents for a tenant.
 * Steps:    1. GET /api/tenants/{tenantId}/fee-schedules.
 * Inputs:   tenantId.
 * Outputs:  server payload — either an array of documents or { documents: [...] }.
 * Failure Handling: Throws on non-OK HTTP; caller shows an error state.
 * Idempotency: REQUIRED — read-only, safe to repeat.
 */
export async function listFeeScheduleDocuments(tenantId: string): Promise<any> {
  return apiFetch(`/api/tenants/${tenantId}/fee-schedules`);
}

/**
 * @process getFeeScheduleDocument
 * Purpose: Fetch one fee schedule document, including its rule set — used to
 *          lazily load rules when a row is expanded.
 * Steps:    1. GET /api/tenants/{tenantId}/fee-schedules/{docId}.
 * Inputs:   tenantId, docId.
 * Outputs:  server payload — { rules } or { document: { rules } }.
 * Failure Handling: Throws on non-OK HTTP; caller leaves rules unloaded.
 * Idempotency: REQUIRED — read-only, safe to repeat.
 */
export async function getFeeScheduleDocument(tenantId: string, docId: string): Promise<any> {
  return apiFetch(`/api/tenants/${tenantId}/fee-schedules/${docId}`);
}
